import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Container,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate, useLocation } from "react-router-dom";
import BdrMenu from "../../../../components/BdrMenu/BdrMenu";
import DriveTimePinChip from "../DriveTime/DriveTimePinChip";
import ClientsNavMenu from "../ClientsNavMenu/ClientsNavMenu";
import "./adminNavbar.css";
import useMediaQuery from "@mui/material/useMediaQuery";
import Auth from "../../../../utils/auth";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import { isTestMode } from "../../../../utils/isTestMode";
import WebAuthnRegister from "../WebAuthn/WebAuthnRegister";
import UserInfoModal from "../UserInfoModal/UserInfoModal";
import capitalizeFirstLetters from "../../../../utils/formatters/capitalizeFirstLetters";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_CURRENT_USER,
  GET_WEBSITE_VERSION,
  GET_PENDING_JOB_FORM_COUNT,
  GET_PENDING_REFERRAL_COUNT,
  GET_OPEN_SHIFT,
  GET_RECENT_CLIENTS,
} from "../../../../utils/GraphQL/queries";
import EventDetailPopup from "../EventDetailPopup/EventDetailPopup";
import { useTourModal } from "../../../../tours/tourModals";
import { useDriveTimePins } from "../../../../utils/DriveTimePin/DriveTimePinContext";
import { DEMO_START_PLACE, demoVisitItem } from "../DriveTime/driveTimeDemo";
import {
  CLOCK_IN_NOW,
  CLOCK_OUT_NOW,
} from "../../../../utils/GraphQL/mutations";
import CalendarPage from "../../AdminPages/CalendarPage/CalendarPage";
import {
  useSSEListener,
  useSSEEvicted,
  SSE_RECONNECTED,
} from "../../../../utils/SSE/SSEContext";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { hasRole, hasAnyRole } from '../../../../utils/permissions';
import { lockBodyScroll, releaseBodyScroll } from "../../../../utils/bodyScrollLock";
import { useTours } from "../../../../tours/TourContext";
import { useTourAction } from "../../../../tours/tourActions";

const getPages = (permission) => {
  let pages = [];
  let manageSubmenu = [];
  let emailsSubmenu = [];
  let paymentsSubmenu = [];

  if (hasRole(permission, 'admin')) {
      pages = [
        {
          // 2026-05-15 — page only renders the tasks board, so the
          // nav label + canonical route both read "Tasks". The legacy
          // `/bdr-office/dashboard` URL is kept as a redirect in
          // App.js so old bookmarks still resolve.
          name: "Tasks",
          href: "/bdr-office/tasks",
        },
        {
          name: "Calendar",
          action: "openCalendar",
        },
        {
          name: "Job Forms",
          href: "/bdr-office/job-form-review",
        },
        {
          name: "Referrals",
          href: "/bdr-office/referrals",
        },
        {
          name: "Manage",
        },
        {
          name: "Clients",
        },
        {
          name: "Emails",
          href: "/bdr-office/email-inbox",
        },
        {
          name: "Messages",
          href: "/bdr-office/messages",
        },
        {
          name: "Settings",
        },
      ];

      manageSubmenu = [
        {
          name: "Business Ledger",
          href: "/bdr-office/business-ledger",
        },
        {
          name: "Payroll Report",
          href: "/bdr-office/payroll-report",
        },
        {
          name: "Process Payment",
          href: "/bdr-office/payments/process-payment",
        },
        {
          name: "Transactions",
          href: "/bdr-office/payments/transactions",
        },
        {
          name: "Image Groups",
          href: "/bdr-office/img-groups",
        },
        {
          name: "Metrics",
          href: "/bdr-office/metrics",
        },
        {
          name: "Shop Inv.",
          href: "/bdr-office/shop-inventory",
        },
        {
          name: "Maint. Reminders",
          href: "/bdr-office/maintenance-reminders",
        },
        {
          name: "Defaults Setup",
          href: "/bdr-office/defaults-setup",
        },
        {
          name: "Users",
          href: "/bdr-office/manage-users",
        },
        {
          // Feature-tour progress per person (registry §36.10).
          name: "Walkthroughs",
          href: "/bdr-office/walkthroughs",
        },
      ];

      if (hasRole(permission, 'developer')) {
        manageSubmenu.push({
          name: "FCM Tokens",
          href: "/bdr-office/fcm-tokens",
        });
        // Developer-gated during rollout (matches the route guard in App.js).
        manageSubmenu.push({
          name: "Classify Doors",
          href: "/bdr-office/classification-review",
        });
        // Per-user theming Designer — developer-only for now; route is
        // guarded by <Protected permCheck={["developer"]}> in App.js.
        manageSubmenu.push({
          name: "App Designer",
          href: "/bdr-office/theme-designer",
        });
      }

      emailsSubmenu = [
        {
          name: "Inbox",
          href: "/bdr-office/email-inbox",
        },
        {
          name: "Sent Emails",
          href: "/bdr-office/email-sent",
        },
        {
          name: "Unsubbed/Blocked Emails",
          href: "/bdr-office/email-blocked",
        },
      ];
  } else if (hasRole(permission, 'refinisher')) {
      pages = [
        {
          // 2026-05-15 — page only renders the tasks board, so the
          // nav label + canonical route both read "Tasks". The legacy
          // `/bdr-office/dashboard` URL is kept as a redirect in
          // App.js so old bookmarks still resolve.
          name: "Tasks",
          href: "/bdr-office/tasks",
        },
        // No web calendar for a plain refinisher (owner 2026-09-16) — they use
        // the mobile app's calendar. A refinisher who also holds office keeps
        // it (this branch wins over the office branch below). The navbar's
        // "openCalendar" action is the only way into CalendarPage.
        ...(hasRole(permission, 'office')
          ? [
              {
                name: "Calendar",
                action: "openCalendar",
              },
            ]
          : []),
        {
          name: "Payments",
        },
        {
          name: "Update List",
          href: "/bdr-office/update-list",
        },
        {
          name: "Settings",
        },
      ];

      // Transactions (getStripeTransactions) has been admin/office-gated on
      // the server since 2026-08-06, so a plain refinisher can't load it. Only
      // offer it when this refinisher ALSO holds office (this branch wins over
      // the office branch below; admins never reach it).
      paymentsSubmenu = [
        {
          name: "Process Payment",
          href: "/bdr-office/payments/process-payment",
        },
        ...(hasRole(permission, 'office')
          ? [
              {
                name: "Transactions",
                href: "/bdr-office/payments/transactions",
              },
            ]
          : []),
      ];
  } else if (hasRole(permission, 'office')) {
      pages = [
        {
          // 2026-05-15 — page only renders the tasks board, so the
          // nav label + canonical route both read "Tasks". The legacy
          // `/bdr-office/dashboard` URL is kept as a redirect in
          // App.js so old bookmarks still resolve.
          name: "Tasks",
          href: "/bdr-office/tasks",
        },
        {
          name: "Calendar",
          action: "openCalendar",
        },
        {
          name: "Job Forms",
          href: "/bdr-office/job-form-review",
        },
        {
          name: "Referrals",
          href: "/bdr-office/referrals",
        },
        {
          name: "Manage",
        },
        {
          name: "Clients",
        },
        {
          name: "Emails",
          href: "/bdr-office/email-inbox",
        },
        {
          name: "Messages",
          href: "/bdr-office/messages",
        },
        {
          name: "Settings",
        },
      ];

      emailsSubmenu = [
        {
          name: "Inbox",
          href: "/bdr-office/email-inbox",
        },
        {
          name: "Sent Emails",
          href: "/bdr-office/email-sent",
        },
      ];

      manageSubmenu = [
        {
          name: "Process Payment",
          href: "/bdr-office/payments/process-payment",
        },
        {
          name: "Transactions",
          href: "/bdr-office/payments/transactions",
        },
        {
          name: "Image Groups",
          href: "/bdr-office/img-groups",
        },
        {
          name: "Defaults Setup",
          href: "/bdr-office/defaults-setup",
        },
        {
          name: "Shortcuts",
          href: "/bdr-office/shortcuts",
        },
      ];
  } else {
      pages = [
        {
          // 2026-05-15 — page only renders the tasks board, so the
          // nav label + canonical route both read "Tasks". The legacy
          // `/bdr-office/dashboard` URL is kept as a redirect in
          // App.js so old bookmarks still resolve.
          name: "Tasks",
          href: "/bdr-office/tasks",
        },
        {
          name: "Settings",
        },
      ];
  }

  return { pages, emailsSubmenu, manageSubmenu, paymentsSubmenu };
};

const AdminNavbar = ({ changeLoggedIn }) => {
  const [anchorElNav, setAnchorElNav] = useState(false);
  const [emailAnchorEl, setEmailAnchorEl] = useState(false);
  /* Clients is a DROPDOWN, not a link: the page it used to point at held only
     a search field, a create button and a recents list, so it is a launcher
     and now lives in the menu. `/bdr-office/clients` is gone. */
  const [clientsAnchorEl, setClientsAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(false);
  const [endpointDialogOpen, setEndpointDialogOpen] = useState(false);
  const [paymentsAnchorEl, setPaymentsAnchorEl] = useState(false);
  const [manageAnchorEl, setManageAnchorEl] = useState(false);
  // Developer-only "Public V2" preview menu (dev-gated routes under /dev-preview)
  const [v2AnchorEl, setV2AnchorEl] = useState(false);
  const subAnchor = useRef();
  const [navPages, setNavPages] = useState([]);
  const [emailSubPages, setEmailSubPages] = useState([]);
  const [manageSubPages, setManageSubPages] = useState([]);
  const [paymentsSubPages, setPaymentsSubPages] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * Which nav item is the page you are on. The bar never showed this — every
   * item looked identical whichever page was open, so the nav told you where
   * you could go but never where you were. Prefix match, not equality, so a
   * detail route (/clients/view-client/:id) still lights up "Clients".
   */
  const isActiveHref = (href) =>
    typeof href === "string" &&
    href.length > 1 &&
    location.pathname.startsWith(href);

  /*
   * A dropdown parent (Manage, Emails, Payments) lights up when any route
   * INSIDE it is open. Without this the bar went blank the moment you opened
   * anything nested — the pages most likely to be deep in a workflow were the
   * ones that told you nothing about where you were.
   *
   * A route listed under two menus lights both, which is honest: it really
   * does live in both.
   */
  const isSectionActive = (pageName) => {
    /*
     * Clients is a dropdown with no sub-PAGES — its menu is a search, a create
     * action and a recents list, none of which are routes. So it lights up on
     * the path prefix instead: anything under /bdr-office/clients, which in
     * practice means a client console. Without this the bar would go blank
     * exactly where the console is deepest in a workflow, which is the thing
     * the comment above says this function exists to prevent.
     */
    if (pageName === "Clients") return isActiveHref("/bdr-office/clients");

    const sub =
      pageName === "Emails"
        ? emailSubPages
        : pageName === "Manage"
        ? manageSubPages
        : pageName === "Payments"
        ? paymentsSubPages
        : [];
    return (sub || []).some((item) => isActiveHref(item?.href));
  };
  const matchesMQ = useMediaQuery("(min-width: 900px)");
  const [userInfo, setUserInfo] = useState();
  const [profileName, setProfileName] = useState("");
  const [websiteVersion, setWebsiteVersion] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [perm, setPerm] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  /*
   * A specific calendar event somebody asked to see (2026-10-07) — currently
   * the Tasks console's Avail. Sooner list.
   *
   * A window CustomEvent, not a route and not context: the calendar is a modal
   * owned by THIS component, and the navbar is a SIBLING of <Routes>, so a page
   * inside the router cannot reach this state by any ordinary means. The same
   * shape `bdr:clockin-changed` already uses below.
   */
  const [calendarFocus, setCalendarFocus] = useState(null);
  const sseEvicted = useSSEEvicted();
  const { handleSnack } = useSnackbar();
  // Feature tours (registry §36): the Help item only exists once the user has
  // a walkthrough; the badge counts unseen ones on the profile button + item.
  const { hasTours, newCount: tourNewCount, openHub } = useTours();
  // "web:avail-sooner" walkthrough: open the calendar on the week view. Gated
  // like the nav entry — a plain refinisher has no web calendar (2026-09-16),
  // and this must not become a second way in.
  // Reads the token, not the `perm` state: that is set by a mount effect, and
  // an action run before it lands would silently do nothing (modal_missing).
  useTourAction("calendar-open", () => {
    const permission = perm || Auth.getProfile()?.data?.permission;
    if (!hasAnyRole(permission, ["admin", "office"])) return;
    setCalendarFocus(null);
    setCalendarOpen(true);
  });

  /*
   * "web:drive-time-pins" walkthrough (registry §17.10 / §36.9). Registered
   * HERE because the navbar is mounted on every page, so these run wherever
   * the tour is — and the demo visit popup, like the calendar, is hosted by
   * the navbar so it exists over the calendar on any page.
   *
   * open-recent-client: the one-click start still shows a customer's page —
   * the most recently viewed one. Recents are global, so there is almost
   * always one; with none the step is `optional` and the tour carries on.
   * The hop reads the client page in peek mode (no "recently viewed" write).
   */
  const pins = useDriveTimePins();
  const [queryRecentClients] = useLazyQuery(GET_RECENT_CLIENTS, {
    fetchPolicy: "network-only",
  });
  useTourAction("open-recent-client", () => {
    queryRecentClients()
      .then(({ data }) => {
        const first = (data?.getRecentClients?.clientIds || []).find((c) => c && c._id);
        if (first) navigate(`/bdr-office/clients/view-client/${first._id}`);
      })
      .catch(() => {});
  });
  // The chip step must have something to show even when the client-page hop
  // was skipped: park the user's pins and show a demo start (restored at the
  // end of the tour by the provider's "drive-time-demo" tag).
  useTourAction("drive-time-demo-ensure-pin", () => {
    if (!pins.demo) pins.beginDemo(DEMO_START_PLACE);
  });
  const [tourDemoPopup, setTourDemoPopup] = useState(false);
  const [demoVisit, setDemoVisit] = useState(null);
  useTourAction("drive-time-demo-popup", () => {
    if (tourDemoPopup) return; // Back re-runs the action; one popup
    setDemoVisit(demoVisitItem());
    setTourDemoPopup(true);
  });
  useTourModal("event-popup-demo", tourDemoPopup, () => setTourDemoPopup(false));

  // NOTE: calendar_update / email_update / go_by_completed toasts moved to the
  // always-mounted GlobalSSEToasts service (utils/SSE/GlobalSSEToasts.jsx) so
  // they fire on every route, not just where this navbar is mounted. This
  // navbar keeps only the listeners that drive navbar-local state (pending
  // badge counts, clock state).

  const handleJobFormSSEUpdate = useCallback((data) => {
    // Toast moved to GlobalSSEToasts; navbar only maintains the pending badge.
    if (data.pendingJobFormCount != null) {
      setPendingJobFormCount(data.pendingJobFormCount);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Referral system SSE listener. Submit toast moved to GlobalSSEToasts; navbar
  // keeps the pending badge count (allowSelfDeliver lets an admin's other tabs
  // decrement instantly when they complete one).
  const handleReferralSSEUpdate = useCallback((data) => {
    if (data.pendingReferralCount != null) {
      setPendingReferralCount(data.pendingReferralCount);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * SSE has no backfill, so counts missed while the socket was down have to be
   * re-read on reconnect. Without this the badge silently keeps a stale number
   * — and since the Tasks console's count tiles DO reconcile on reconnect, the
   * two would disagree on screen, which is the failure the tiles were designed
   * to avoid in the first place. Badge and tile reconcile together or not at
   * all.
   */
  const handleCountsReconnect = useCallback(() => {
    /* Mirror the mount path's guards. Without them a refinisher's navbar fires
       two guaranteed-Unauthorized calls on every reconnect — recurring error
       traffic the mount path deliberately avoids. */
    if (!hasAnyRole(perm, ["admin", "office"])) return;
    fetchPendingCount();
    fetchPendingReferralCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perm]);

  useSSEListener("job_form_update", handleJobFormSSEUpdate);
  useSSEListener("referral_update", handleReferralSSEUpdate);
  useSSEListener(SSE_RECONNECTED, handleCountsReconnect);

  // Cross-tab clock state sync. Fires on every tab of the same user when
  // any tab clocks in/out. Originator tab is identified by
  // `data.originConnectionId` and is suppressed (its local state is
  // already authoritative). Other tabs refetch the open shift to flip the
  // Clock In/Out menu label.
  const handleTimeEntrySSEUpdate = useCallback((data) => {
    const myConnId = sessionStorage.getItem("bdr_sse_connection_id");
    if (data?.originConnectionId && myConnId && data.originConnectionId === myConnId) {
      return;
    }
    if (!userInfo?._id) return;
    fetchOpenShift({ variables: { userId: userInfo._id } })
      .then(({ data: shiftData }) => {
        setOpenShift(shiftData?.getOpenShift || null);
      })
      .catch(() => {
        // Network blip — leave state as-is; next interaction will refresh.
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?._id]);

  useSSEListener("time_entry_update", handleTimeEntrySSEUpdate);

  const [pendingJobFormCount, setPendingJobFormCount] = useState(0);
  const [pendingReferralCount, setPendingReferralCount] = useState(0);
  // Open TimeEntry for the logged-in user, if any. Drives the Clock In/Out
  // menu item label inside the Settings submenu. Office-only.
  const [openShift, setOpenShift] = useState(null);
  const [clockBusy, setClockBusy] = useState(false);
  const [getCurrentUser] = useLazyQuery(GET_CURRENT_USER, {
    fetchPolicy: "network-only",
  });
  const [getWebsiteVersion] = useLazyQuery(GET_WEBSITE_VERSION, {
    fetchPolicy: "network-only",
  });
  const [fetchPendingCount] = useLazyQuery(GET_PENDING_JOB_FORM_COUNT, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.getPendingJobFormCount != null) {
        setPendingJobFormCount(data.getPendingJobFormCount);
      }
    },
  });
  const [fetchPendingReferralCount] = useLazyQuery(GET_PENDING_REFERRAL_COUNT, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.getPendingReferralCount != null) {
        setPendingReferralCount(data.getPendingReferralCount);
      }
    },
  });
  const [fetchOpenShift] = useLazyQuery(GET_OPEN_SHIFT, {
    fetchPolicy: "network-only",
  });
  const [clockInMutation] = useMutation(CLOCK_IN_NOW);
  const [clockOutMutation] = useMutation(CLOCK_OUT_NOW);

  // Same-tab clock-in sync. When the office ClockInReminder modal (a separate
  // component) clocks in, its SSE broadcast is self-suppressed by this tab
  // (origin match), so the time_entry_update listener above won't fire here.
  // The modal dispatches this window event instead; refetch the open shift so
  // the Settings menu label flips to Clock Out without a page refresh.
  useEffect(() => {
    const onClockInChanged = () => {
      if (!userInfo?._id) return;
      fetchOpenShift({ variables: { userId: userInfo._id } })
        .then(({ data: shiftData }) => {
          setOpenShift(shiftData?.getOpenShift || null);
        })
        .catch(() => {
          // Network blip — next interaction will refresh.
        });
    };
    // Open the calendar on a given event. Detail: { dayMs, eventId }.
    const onOpenCalendarEvent = (e) => {
      const detail = e?.detail || {};
      if (!detail.eventId) return;
      setCalendarFocus({ dayMs: detail.dayMs, eventId: detail.eventId });
      setCalendarOpen(true);
    };
    window.addEventListener("bdr:open-calendar-event", onOpenCalendarEvent);
    window.addEventListener("bdr:clockin-changed", onClockInChanged);
    // A BLOCK body, not an expression arrow: the original removed one listener
    // as a single expression, and a second line appended to that is simply
    // unreachable — which would have leaked a listener on every remount.
    return () => {
      window.removeEventListener("bdr:open-calendar-event", onOpenCalendarEvent);
      window.removeEventListener("bdr:clockin-changed", onClockInChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?._id]);

  /* Lock + release in the cleanup, so the pair is balanced by construction.
     The old shape released on every render where the modal was shut —
     including before it had ever opened — which drives a shared count
     negative. See utils/bodyScrollLock.js. */
  useEffect(() => {
    if (!modalOpen) return undefined;
    lockBodyScroll();
    return () => releaseBodyScroll();
  }, [modalOpen]);

  useEffect(() => {
    getPagesArrAndSaveUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Version DISPLAY only (profile menu "Version x.y.z"). The old
  // silent-hard-reload mechanism (15-min poll + bdr_website_version
  // localStorage + window.location.reload on mismatch) is superseded by
  // DeployRefreshManager (deploy-warning countdown + banner-with-grace,
  // build-id based). Guard on a truthy version so a failed query can't
  // blank the display or loop the effect.
  useEffect(() => {
    (async () => {
      const { data } = await getWebsiteVersion();
      const version = data?.getWebsiteVersion?.version;
      if (version) setWebsiteVersion(version);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPagesArrAndSaveUserInfo = async () => {
    const { data } = await getCurrentUser();

    if (!data?.getCurrentUser) {
      return;
    }

    const user = { ...data?.getCurrentUser };
    console.log();
    const lastInitial = user?.lastName?.trim()?.split("")[0];
    const profileName = capitalizeFirstLetters(
      `${user?.firstName} ${lastInitial}.`
    );

    const pageArr = getPages(user.permission);
    setPerm(user.permission);
    setNavPages([...pageArr?.pages]);
    setEmailSubPages([...pageArr?.emailsSubmenu]);
    setManageSubPages([...pageArr?.manageSubmenu]);
    setPaymentsSubPages([...pageArr?.paymentsSubmenu]);
    setUserInfo(user);
    setProfileName(profileName);
    setModalOpen(user?.initLogin);

    // Fetch pending job form count for admin/office badge
    if (hasAnyRole(user.permission, ['admin', 'office'])) {
      fetchPendingCount();
    }
    // Referral system: fetch pending referral count for the badge
    // (admin / office / developer per the route's permCheck).
    if (hasAnyRole(user.permission, ['admin', 'office', 'developer'])) {
      fetchPendingReferralCount();
    }

    // Fetch the user's open shift (if any) so the Settings menu can render
    // Clock In vs. Clock Out · Xh Ym. Office + developer roles only —
    // admins and refinishers don't clock in.
    if (hasAnyRole(user.permission, ['office', 'developer'])) {
      try {
        const { data: shiftData } = await fetchOpenShift({
          variables: { userId: user._id },
        });
        setOpenShift(shiftData?.getOpenShift || null);
      } catch {
        // Network blip; menu will still render Clock In and the user can try.
      }
    }
  };

  // Format epoch-ms duration as "Xh Ym" (or "Ym" under one hour).
  const formatElapsed = (ms) => {
    if (!ms || ms < 0) return "0m";
    const totalMin = Math.floor(ms / 60000);
    const hr = Math.floor(totalMin / 60);
    const mn = totalMin % 60;
    return hr > 0 ? `${hr}h ${mn}m` : `${mn}m`;
  };

  const handleClockIn = async () => {
    if (clockBusy || !userInfo?._id) return;
    setClockBusy(true);
    try {
      const { data } = await clockInMutation({
        variables: { userId: userInfo._id },
      });
      if (data?.clockInNow) {
        setOpenShift(data.clockInNow);
        handleSnack("Clocked in", 2000);
      }
    } catch (err) {
      handleSnack(err?.message || "Clock-in failed", 4000);
    } finally {
      setClockBusy(false);
    }
  };

  const handleClockOut = async () => {
    if (clockBusy || !userInfo?._id) return;
    setClockBusy(true);
    try {
      const { data } = await clockOutMutation({
        variables: { userId: userInfo._id },
      });
      if (data?.clockOutNow) {
        setOpenShift(null);
        handleSnack("Clocked out", 2000);
      }
    } catch (err) {
      handleSnack(err?.message || "Clock-out failed", 4000);
    } finally {
      setClockBusy(false);
    }
  };

  const updateModalHandler = (state) => {
    setModalOpen(state);
  };

  const logoutHandler = async () => {
    const loggedOut = await Auth.logout();

    const adminNavOut = await changeLoggedIn(false);

    if (loggedOut && adminNavOut) {
      navigate("/login-portal");
    }
  };

  const handleEndpointToggle = () => {
    const newEndpoint = isTestMode() ? "/graphql" : "/tgraphql";
    sessionStorage.setItem("bdr_gql_endpoint", newEndpoint);
    setEndpointDialogOpen(false);
    window.location.reload();
  };

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(false);
  };

  /*
   * Takes the anchor ELEMENT rather than an event. BdrMenu hands an item its
   * own DOM node, because a data-driven item list has no `event.currentTarget`
   * to reach for — and a submenu needs something to hang off.
   */
  const handleOpenSubMenu = (el, targetName) => {
    if (targetName === "ClientsSub") {
      setClientsAnchorEl(el);
      return;
    }

    if (targetName === "EmailsSub") {
      setEmailAnchorEl(el);
      return;
    }

    if (targetName === "ManageSub") {
      setManageAnchorEl(el);
      return;
    }

    if (targetName === "SettingsSub") {
      setSettingsAnchorEl(el);
      return;
    }

    if (targetName === "PaymentsSub") {
      setPaymentsAnchorEl(el);
      return;
    }
  };

  /*
   * A submenu opened from the MOBILE nav menu is a menu on top of a menu, so
   * it takes the next nesting rung — its backdrop then sits above the nav
   * menu's panel, and clicking the nav menu dismisses just the submenu.
   * Opened from the desktop bar there is nothing underneath, so it is 0.
   */
  const subMenuDepth = anchorElNav ? 1 : 0;

  const handleCloseSubMenu = () => {
    setClientsAnchorEl(null);
    setEmailAnchorEl(null);
    setManageAnchorEl(null);
    setSettingsAnchorEl(null);
    setPaymentsAnchorEl(null);
  };

  return (
    <AppBar
      /*
       * STICKY, not fixed. Sticky stays in normal flow, so it still consumes
       * real height — which is what `useNavHeightVar` measures to publish
       * `--bdr-nav-h`, and what `.scc-page`'s `calc(100dvh - var(--bdr-nav-h))`
       * subtracts. Going `fixed` would take it out of flow and every
       * viewport-locked page would size itself as though the bar were not
       * there, then render underneath it.
       */
      position="sticky"
      /* No elevation. MUI paints AppBar's shadow as a full-bleed band across
         the whole width, which is the one thing the floating glass bar should
         not have behind it. `backgroundImage` goes too — MUI layers a Paper
         overlay there that a transparent backgroundColor does not clear. */
      elevation={0}
      sx={{
        top: 0,
        backgroundColor: "transparent",
        backgroundImage: "none",
        boxShadow: "none",
        /* Above page content, far below the modal ladder (--bdr-z-modal is
           1300). A bar that can cover a dialog is worse than one that scrolls. */
        zIndex: 10,
      }}
    >
      <Container
        maxWidth="100vw"
        id="admin-nav-height-marker"
        className="admin-navbar"
      >
        {/* User Info Update Modal */}
        {modalOpen && (
          <UserInfoModal
            user={userInfo}
            updateModalHandler={updateModalHandler}
          />
        )}

        {/* MUI's Toolbar floors itself at 64px (56 on mobile). The bar holds
            one row of small buttons, so that floor was pure dead height on a
            screen that is shorter than it is wide. */}
        <Toolbar disableGutters sx={{ minHeight: { xs: 0, md: 0 } }}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: { md: "column", xl: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "center", md: "flex-start", xl: "center" },
            }}
          >
            {matchesMQ ? null : (
              <div className="admin-nav-hdr-container">
                <h1 className="admin-nav-hdr">BDR Admin</h1>
              </div>
            )}

            {/* Drive-time pins readout (registry §17.10). Renders nothing
                until something is pinned, so the bar is unchanged otherwise.
                Inline here above 600px; its phone twin is the full-width row
                under the Toolbar. */}
            <DriveTimePinChip className="dt-chip--inline" />

            {/* "Public V2" preview menu — a separate hamburger holding links
                to the v2 public pages (/dev-preview/*).

                DEVELOPER ONLY (2026-10-07, owner request). It was
                developer/office/admin, so every office and admin user carried
                a menu into a preview of pages they have no reason to review —
                and the one entry that mattered to them, "Style Reference", was
                already developer-gated inside it. This hides the MENU; the
                /dev-preview routes keep whatever gates they already had. */}
            {hasRole(perm, "developer") && (
              <Box sx={{ display: "flex", alignItems: "center", m: "5px 6px" }}>
                <IconButton
                  size="small"
                  /* `color="inherit"` for the same reason the nav links carry
                     it: MuiIconButton-root sets its own colour and emotion
                     injects after this sheet, so the class alone loses. */
                  color="inherit"
                  aria-label="Public V2 preview menu"
                  aria-haspopup="true"
                  onClick={(e) => setV2AnchorEl(e.currentTarget)}
                  className="admin-nav-v2-btn"
                >
                  <MenuIcon fontSize="small" />
                  <span style={{ fontSize: 12, marginLeft: 4, fontWeight: 700 }}>
                    V2
                  </span>
                </IconButton>
                <BdrMenu
                  anchorEl={v2AnchorEl}
                  ariaLabel="Public V2 preview"
                  onClose={() => setV2AnchorEl(false)}
                  items={[
                    /* No longer conditional: the menu itself is developer-only
                       now, so this check could only ever be true. The route is
                       `permCheck="developer"` either way. */
                    { label: "Style Reference", href: "/bdr-office/style" },
                    { label: "V2 Preview — Index", href: "/dev-preview" },
                    { label: "Home", href: "/dev-preview/home" },
                    { label: "Request a Quote", href: "/dev-preview/quote" },
                    { label: "Our Doors", href: "/dev-preview/our-doors" },
                    { label: "Contact", href: "/dev-preview/contact" },
                    { label: "Privacy Policy", href: "/dev-preview/privacy-policy" },
                    { label: "Terms of Service", href: "/dev-preview/terms-of-service" },
                  ].map((p) => ({
                    key: p.href,
                    label: p.label,
                    onClick: () => {
                      navigate(p.href);
                      setV2AnchorEl(false);
                    },
                  }))}
                />
              </Box>
            )}

            {/* Small Box Display */}
            <Box
              className="admin-nav-group"
              sx={{
                display: { xs: "flex", md: "none" },
                margin: "5px 0px",
              }}
            >
              <IconButton
                size="small"
                aria-label="page menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              {/* The phone nav menu. Its four submenu rows keep it OPEN and
                  open a second menu at depth 1 on top of it — see
                  `subMenuDepth`. */}
              <BdrMenu
                anchorEl={anchorElNav}
                ariaLabel="Navigation"
                onClose={handleCloseNavMenu}
                items={(navPages || []).map((page) => {
                  const thisPage =
                    page?.name === "Settings" ? profileName : page?.name;

                  if (page?.action === "openCalendar") {
                    return {
                      key: thisPage,
                      label: thisPage,
                      onClick: () => {
                        setCalendarOpen(true);
                        handleCloseNavMenu();
                      },
                    };
                  }

                  const isSubMenu =
                    page?.name === "Clients" ||
                    page?.name === "Emails" ||
                    page?.name === "Manage" ||
                    page?.name === "Settings" ||
                    page?.name === "Payments";

                  if (!isSubMenu) {
                    const badge =
                      (page.name === "Job Forms" && pendingJobFormCount) ||
                      (page.name === "Referrals" && pendingReferralCount) ||
                      0;
                    return {
                      key: thisPage,
                      label: (
                        <span className="admin-nav-menu-label">
                          {thisPage}
                          {badge > 0 && <span className="nav-badge">{badge}</span>}
                        </span>
                      ),
                      onClick: () => {
                        navigate(page.href);
                        handleCloseNavMenu();
                      },
                    };
                  }

                  /*
                   * Clients is the one submenu that becomes a MODAL on a phone
                   * (see ClientsNavMenu), so it must NOT keepOpen: a menu sits
                   * at --bdr-z-popover (1000001) and a modal tops out at 1900,
                   * so leaving the nav menu up would paint it over the modal.
                   * Closing here is what lets the modal be seen at all.
                   */
                  if (page?.name === "Clients") {
                    return {
                      key: page?.name,
                      label: (
                        <span className="admin-nav-menu-label">{thisPage}</span>
                      ),
                      onClick: (el) => {
                        handleOpenSubMenu(el, "ClientsSub");
                        handleCloseNavMenu();
                      },
                    };
                  }

                  return {
                    key: page?.name,
                    // keepOpen: this opens a submenu ON TOP of this menu. Closing
                    // first would clear `anchorElNav` and take the parent with it.
                    keepOpen: true,
                    label: (
                      <span className="admin-nav-menu-label">
                        {thisPage}
                        <ArrowDropDownIcon fontSize="small" />
                        {page?.name === "Settings" && tourNewCount > 0 && (
                          <span className="nav-badge">{tourNewCount}</span>
                        )}
                      </span>
                    ),
                    onClick: (el) => handleOpenSubMenu(el, `${page?.name}Sub`),
                  };
                })}
              />
            </Box>

            {/* Large Box Display */}
            <Box
              className="admin-nav-group"
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "space-around",
                /* Hugs its links at xl rather than spanning — owner. Still
                   full width in the md band, where there is no room to spare. */
                width: { md: "100%", xl: "fit-content" },
                margin: "5px 0px",
              }}
              textAlign="center"
            >
              {navPages.map((page) => {
                const thisPage =
                  page?.name === "Settings" ? profileName : page?.name;

                if (page?.action === "openCalendar") {
                  return (
                    <Button
                      key={thisPage}
                      color="inherit"
                      className="admin-nav-link"
                      onClick={() => setCalendarOpen(true)}
                      sx={{
                        mx: { xl: 0.25 },
                        px: 1.25,
                        display: "block",
                        fontSize: "12px",
                      }}
                    >
                      {thisPage}
                    </Button>
                  );
                }

                if (
                  page?.name !== "Clients" &&
                  page?.name !== "Emails" &&
                  page?.name !== "Manage" &&
                  page?.name !== "Settings" &&
                  page?.name !== "Payments"
                ) {
                  return (
                    <Button
                      key={thisPage}
                      color="inherit"
                      className={`admin-nav-link${
                        isActiveHref(page.href) ? " is-active" : ""
                      }`}
                      aria-current={isActiveHref(page.href) ? "page" : undefined}
                      onClick={() => {
                        navigate(page.href);
                      }}
                      sx={{
                        mx: { xl: 0.25 },
                        px: 1.25,
                        display: "block",
                        fontSize: "12px",
                        position: "relative",
                      }}
                    >
                      {thisPage}
                      {page.name === "Job Forms" && pendingJobFormCount > 0 && (
                        <span className="nav-badge">{pendingJobFormCount}</span>
                      )}
                      {page.name === "Referrals" && pendingReferralCount > 0 && (
                        <span className="nav-badge">{pendingReferralCount}</span>
                      )}
                    </Button>
                  );
                }

                return (
                  <Button
                    ref={subAnchor}
                    key={thisPage}
                    color="inherit"
                    className={`admin-nav-link${
                      isSectionActive(page?.name) ? " is-active" : ""
                    }`}
                    aria-current={
                      isSectionActive(page?.name) ? "true" : undefined
                    }
                    onClick={(event) => {
                      handleOpenSubMenu(event.currentTarget, `${page?.name}Sub`);
                    }}
                    sx={{
                      mx: { xl: 0.25 },
                        px: 1.25,
                      display: "block",
                      fontSize: "12px",
                      position: "relative",
                    }}
                  >
                    {thisPage} <ArrowDropDownIcon />
                    {page?.name === "Settings" && tourNewCount > 0 && (
                      <span className="nav-badge">{tourNewCount}</span>
                    )}
                  </Button>
                );
              })}
              {/* <WebAuthnRegister inNavSmall={false} /> */}
              {/* <Button
                onClick={logoutHandler}
                sx={{
                  mx: { xl: 0.25 },
                        px: 1.25,
                  color: "white",
                  display: "block",
                  fontSize: "12px",
                }}
              >
                Logout
              </Button> */}
            </Box>
          </Box>
        </Toolbar>

        {/* Drive-time pins, phone layout: its own line under the title so it
            never squeezes the two-word title into a wrap. Inside the
            height-marker Container, so pages still offset for it. */}
        <DriveTimePinChip className="dt-chip--row" />

        {/* Live updates disconnected banner */}
        {sseEvicted && (
          <div className="admin-nav-sse-banner" role="status">
            Live updates paused — too many tabs open
          </div>
        )}

        {/* Clients — the page, as a menu. Owns its own ClientFormModal so
            creating a client works from anywhere in the app. */}
        <ClientsNavMenu
          anchorEl={clientsAnchorEl}
          /*
           * Same nesting rung as every other submenu. On the phone this opens
           * ON TOP of the nav menu, so it needs depth 1 for its backdrop to
           * sit above the nav menu's PANEL — without it the two share a rung,
           * and a tap meant to dismiss the search tore down the nav menu with
           * it.
           */
          depth={subMenuDepth}
          /* Dismiss: this menu only, leaving the nav menu standing. */
          onClose={handleCloseSubMenu}
          /* Leaving for somewhere else: take the nav menu down too, or it
             stays open over the page just navigated to. */
          onNavigated={() => {
            handleCloseSubMenu();
            handleCloseNavMenu();
          }}
        />

        {/* Emails Nav Submenu */}
        <BdrMenu
          anchorEl={emailAnchorEl}
          ariaLabel="Emails"
          depth={subMenuDepth}
          onClose={handleCloseSubMenu}
          items={(emailSubPages || []).map((page, index) => ({
            key: page?.name + index,
            label: page?.name,
            onClick: () => {
              navigate(page?.href);
              handleCloseSubMenu();
              handleCloseNavMenu();
            },
          }))}
        />

        {/* Settings Nav Submenu */}
        {/* The profile menu. `WebAuthnRegister` is a whole component, not a
            row, so it goes in `children` above the items. */}
        <BdrMenu
          anchorEl={settingsAnchorEl}
          ariaLabel="Account"
          depth={subMenuDepth}
          onClose={handleCloseSubMenu}
          items={[
            {
              label: "Update User",
              onClick: () => {
                handleCloseSubMenu();
                handleCloseNavMenu();
                updateModalHandler(true);
              },
            },
            hasAnyRole(perm, ["office", "developer"]) &&
              (openShift
                ? {
                    label: `Clock Out \u00b7 ${formatElapsed(
                      Date.now() - parseInt(openShift.clockInMs, 10)
                    )}`,
                    danger: true,
                    disabled: clockBusy,
                    onClick: () => {
                      handleClockOut();
                      handleCloseSubMenu();
                      handleCloseNavMenu();
                    },
                  }
                : {
                    label: "Clock In",
                    accent: true,
                    disabled: clockBusy,
                    onClick: () => {
                      handleClockIn();
                      handleCloseSubMenu();
                      handleCloseNavMenu();
                    },
                  }),
            hasAnyRole(perm, ["admin", "office"]) && {
              label: "Time Sheets",
              onClick: () => {
                navigate("/bdr-office/user-hourly-work-sheets");
                handleCloseSubMenu();
                handleCloseNavMenu();
              },
            },
            hasRole(perm, "admin") && {
              label: isTestMode() ? "Switch to Live DB" : "Switch to Test DB",
              onClick: () => {
                handleCloseSubMenu();
                handleCloseNavMenu();
                setEndpointDialogOpen(true);
              },
            },
            hasTours && {
              label: (
                <span className="admin-nav-menu-label">
                  Help &amp; What's New
                  {tourNewCount > 0 && <span className="nav-badge">{tourNewCount}</span>}
                </span>
              ),
              onClick: () => {
                handleCloseSubMenu();
                handleCloseNavMenu();
                openHub(tourNewCount > 0 ? "new" : "help");
              },
            },
            { divider: true },
            /* A caption, not an action — it used to be a MenuItem with no
               onClick, which looked clickable and did nothing. */
            { header: true, label: `Version ${websiteVersion}` },
            { label: "Logout", onClick: logoutHandler },
          ]}
        >
          <WebAuthnRegister inNavSmall={true} />
        </BdrMenu>

        {/* Manage Nav Submenu — the first menu on BdrMenu. MUI's Menu drew a
            white paper with black text over a blue-purple app and could not be
            talked out of it; this one is the app's own lighter container
            surface. The anchor, open state and close handler are unchanged. */}
        <BdrMenu
          anchorEl={manageAnchorEl}
          ariaLabel="Manage"
          depth={subMenuDepth}
          onClose={handleCloseSubMenu}
          items={(manageSubPages || []).map((page, index) => ({
            key: page?.name + index,
            label: page?.name,
            onClick: () => {
              navigate(page?.href);
              handleCloseSubMenu();
              handleCloseNavMenu();
            },
          }))}
        />

        {/* Payments Nav Submenu */}
        <BdrMenu
          anchorEl={paymentsAnchorEl}
          ariaLabel="Payments"
          depth={subMenuDepth}
          onClose={handleCloseSubMenu}
          items={(paymentsSubPages || []).map((page, index) => ({
            key: page?.name + index,
            label: page?.name,
            onClick: () => {
              navigate(page?.href);
              handleCloseSubMenu();
              handleCloseNavMenu();
            },
          }))}
        />
      </Container>

      {/* Calendar Modal */}
      <CalendarPage
        open={calendarOpen}
        onClose={() => {
          setCalendarOpen(false);
          // Cleared on close so re-opening the calendar by hand does not
          // silently re-focus the last event somebody deep-linked to.
          setCalendarFocus(null);
        }}
        focusEvent={calendarFocus}
      />

      {/* The walkthrough's demo visit (registry §17.10): a fixed example
          visit the tour opens over the calendar. Never a real record; its
          buttons are shielded by the tour and its checkbox is never ticked. */}
      {tourDemoPopup && demoVisit && (
        <EventDetailPopup
          item={demoVisit}
          tourDemo
          onClose={() => setTourDemoPopup(false)}
          onEdit={() => {}}
          onViewInvoice={() => {}}
          onViewQuote={() => {}}
        />
      )}

      <BdrModal
        open={endpointDialogOpen}
        onClose={() => setEndpointDialogOpen(false)}
        title="Switch Database"
        variant="panel"
        size="md"
        /* peak: the navbar sits above everything, and this can be opened
           while a page modal is up */
        layer="peak"
        actions={
          <>
            <BdrButton onClick={() => setEndpointDialogOpen(false)}>
              Cancel
            </BdrButton>
            <BdrButton variant="danger" confirm={false} onClick={handleEndpointToggle}>
              {isTestMode() ? "Switch to LIVE" : "Switch to TEST"}
            </BdrButton>
          </>
        }
      >
        <p className="bdr-confirm-note">
          {isTestMode()
            ? "Switch to the LIVE database? This will reload the page."
            : "Switch to the TEST database? This will reload the page."}
        </p>
      </BdrModal>

    </AppBar>
  );
};

export default AdminNavbar;
